import { customColors } from '@apollo/orbit';
import classnames from 'classnames';
import { range, sortBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import tinycolor from 'tinycolor2';

import { format } from 'src/lib/format';

import { Cursor } from '../cursor/Cursor';
import { TraceTreeNode } from '../TraceTree';

import { leftMarginForDepth } from './layoutHelpers';
import { TraceNodeDrawer } from './TraceNodeDrawer';
import { TraceNodeHandle } from './TraceNodeHandle';

const TextNode = React.memo(
  ({
    name,
    depth,
    showingIndent,
    rowHoverColor,
    italic,
  }: {
    name: string;
    depth: number;
    showingIndent?: number;
    rowHoverColor: string;
    italic: boolean;
  }) => {
    const [hovering, setHovering] = useState(false);
    const currentRowBackgroundColor = hovering
      ? // using alpha so that the hover background doesn't cover the row timing
        tinycolor(rowHoverColor).setAlpha(0.2).toString()
      : undefined;

    const rowMouseHandlers = {
      onMouseEnter: () => {
        setHovering(true);
      },
      onMouseLeave: () => {
        setHovering(false);
      },
    };

    return (
      <div
        className={classnames(
          'relative flex items-center border-b border-b-primary leading-none transition-background duration-150',
        )}
        style={{
          gridColumn: '1 / span 4',
          backgroundColor: currentRowBackgroundColor,
        }}
        {...rowMouseHandlers}
      >
        {range(0, depth).map((indentDepth) => (
          <div
            className={classnames('ml-3 h-full w-2 flex-none border-primary', {
              'border-l': indentDepth === showingIndent,
            })}
            key={indentDepth}
          />
        ))}
        <span
          title={name}
          className={classnames('z-10 truncate rounded-sm py-1 pr-3', {
            italic,
          })}
        >
          {name}
        </span>
      </div>
    );
  },
);

export const TraceNode = React.memo(
  ({
    depth = 0,
    node,
    criticalPath,
    requestEnd,
    requestStart,
    showingIndent,
    handleShowIndent,
    rowHoverColor,
    isOpen,
    onOpenChange,
    erroredNodesPathIds,
  }: {
    node: TraceTreeNode;
    requestEnd: number;
    requestStart: number;
    criticalPath: TraceTreeNode[];
    depth?: number;
    showingIndent?: number;
    handleShowIndent?: (showIndent: boolean) => void;
    rowHoverColor: string;
    isOpen: (nodeId: string) => boolean;
    onOpenChange: (nodeId: string) => void;
    erroredNodesPathIds: Set<string>;
  }) => {
    const [hovering, setHovering] = useState(false);
    const [showIndent, setShowIndent] = useState(false);
    const open = isOpen(node.id);

    const currentRowBackgroundColor =
      node.errors.length > 0 && isOpen(node.id)
        ? customColors.bg.error
        : hovering
          ? // using alpha so that the hover background doesn't cover the row timing
            tinycolor(rowHoverColor).setAlpha(0.2).toString()
          : undefined;

    const nodeCanOpen = node.children.length > 0 || node.errors.length > 0;
    const rowMouseHandlers = {
      onMouseEnter: () => {
        setHovering(true);
        if (!open) handleShowIndent?.(true);
      },
      onMouseLeave: () => {
        setHovering(false);
        if (!open) handleShowIndent?.(false);
      },
      onClick: nodeCanOpen
        ? () => {
            if (open) {
              onOpenChange(node.id);
              handleShowIndent?.(true);
            } else {
              onOpenChange(node.id);
              handleShowIndent?.(false);
            }
          }
        : undefined,
    };

    const isPartOfDeferredFetch =
      node?.isDeferredFetch || node.inherited?.isDeferred;
    const isRootNode = depth === 0;
    const requestDuration = requestEnd - requestStart;
    const inferred = node.startTime === node.endTime;

    const [childrenStart, childrenEnd] = useMemo(() => {
      if (node.children.length > 0) {
        // Read children to show a time that represents them all.
        // For non-root nodes, we want to get min/max start/end times from child
        // nodes and this node, but for root nodes, we want to only get the
        // min/max times from children, so we can show the "waiting on server"
        // section on the bar
        const childrenWithDuration = node
          .getDescendants(isRootNode)
          .filter((n) => n.startTime !== n.endTime);

        return [
          Math.min(...childrenWithDuration.map((n) => n.startTime)),
          Math.max(...childrenWithDuration.map((n) => n.endTime)),
        ];
      }
      return [0, 0];
    }, [node, isRootNode]);

    let start = node.startTime;
    let end = node.endTime;
    if (node.isFetch && !!node.subgraphStartTime && !!node.subgraphEndTime) {
      start = node.subgraphStartTime;
      end = node.subgraphEndTime;
    } else if ((!open || isRootNode) && childrenEnd - childrenStart) {
      start = childrenStart;
      end = childrenEnd;
    }

    const duration = end - start;
    const percentTime = (100 * duration) / requestDuration;
    const leftOffset = (100 * (start - requestStart)) / requestDuration;

    return (
      <>
        <div
          className={classnames(
            'relative flex items-center border-b border-b-primary transition-background duration-150',
            { 'cursor-pointer': nodeCanOpen },
          )}
          style={{
            gridColumn: 1,
            backgroundColor: currentRowBackgroundColor,
          }}
          {...rowMouseHandlers}
        >
          {range(0, depth).map((indentDepth) => (
            <div
              className={classnames(
                'ml-4 h-full w-2 flex-none border-primary',
                { 'border-l': indentDepth === showingIndent },
              )}
              key={indentDepth}
            />
          ))}
          {node.children.length > 0 && (
            <div
              className={classnames(
                'absolute bottom-0 h-full w-2 flex-none border-primary',
                { 'border-l': (hovering || showIndent) && open },
              )}
              style={{ left: leftMarginForDepth(depth, 1), top: '50%' }}
            />
          )}
          <TraceNodeHandle
            node={node}
            isOpen={open}
            hovering={hovering}
            showErrorSignifier={erroredNodesPathIds.has(node.id)}
          />
        </div>
        <div
          style={{
            gridColumn: 2,
            backgroundColor: currentRowBackgroundColor,
          }}
          className={classnames(
            'relative flex flex-row items-center border-b border-b-primary py-1 font-mono transition-background duration-150',
            { 'cursor-pointer': nodeCanOpen },
          )}
          {...rowMouseHandlers}
        >
          {/* request time before resolver starts */}
          {isRootNode && (
            <div
              style={{ width: `${leftOffset}%` }}
              className="h-5 rounded-l-sm bg-blilet-lighter transition-size duration-150 ease-out"
            />
          )}

          {/* request time */}
          <div
            style={{
              minWidth: !isRootNode && inferred ? 0 : 4,
              left: `${isRootNode ? 0 : leftOffset}%`,
              width: `${percentTime}%`,
              transitionProperty: 'width, height, left',
            }}
            className={classnames(
              'relative h-5 duration-150 ease-out',
              isRootNode
                ? 'bg-blilet'
                : isPartOfDeferredFetch
                  ? 'bg-blilet-darker'
                  : 'bg-silver-darker',
              { 'rounded-sm': !isRootNode },
            )}
          />

          {/* request time after resolver finishes */}
          {isRootNode && (
            <div
              style={{
                width: `${(100 * (requestEnd - end)) / requestDuration}%`,
              }}
              className="h-5 rounded-r-sm bg-blilet-lighter duration-150 ease-out"
            />
          )}

          <Cursor
            requestStart={requestStart}
            start={start}
            end={end}
            requestEnd={requestEnd}
            isRootNode={isRootNode}
          />

          <div
            className="absolute whitespace-nowrap pl-2 text-sm"
            style={{ left: `${isRootNode ? 100 : leftOffset + percentTime}%` }}
          >
            {(isRootNode || !inferred || !open) &&
              format.ms(isRootNode ? requestDuration : duration)}
          </div>
        </div>
        <div
          className={classnames(
            'relative flex items-center justify-end whitespace-nowrap border-b border-b-primary font-mono text-sm text-secondary transition-background duration-150',
          )}
          style={{ backgroundColor: currentRowBackgroundColor, gridColumn: 3 }}
          {...rowMouseHandlers}
        >
          {!isRootNode &&
            inferred &&
            typeof node.key === 'number' &&
            open &&
            '(array element)'}
        </div>
        <div
          className="flex items-center justify-end whitespace-nowrap border-b border-b-primary  font-mono text-xs transition-background duration-150"
          style={{
            gridColumn: 4,
            backgroundColor: currentRowBackgroundColor,
          }}
          {...rowMouseHandlers}
        >
          {node.cacheMaxAgeMs && format.ms(node.cacheMaxAgeMs)}
        </div>
        <TraceNodeDrawer
          node={node}
          isOpen={open}
          depth={depth}
          showBorder={hovering || showIndent}
        />
        {open &&
          sortBy(node.children, (child) => child.startTime).map((n) => (
            <TraceNode
              requestEnd={requestEnd}
              requestStart={requestStart}
              criticalPath={criticalPath}
              key={n.id}
              node={n}
              depth={depth + 1}
              showingIndent={hovering || showIndent ? depth : showingIndent}
              handleShowIndent={setShowIndent}
              rowHoverColor={rowHoverColor}
              isOpen={isOpen}
              onOpenChange={onOpenChange}
              erroredNodesPathIds={erroredNodesPathIds}
            />
          ))}
        {open &&
          !node.childrenAreFiltered &&
          node.childCount !== undefined &&
          node.children.length < node.childCount && (
            <TextNode
              name={`${node.childCount - node.children.length} ${
                node.childCount - node.children.length === 1
                  ? 'child'
                  : 'children'
              } omitted`}
              depth={depth + 1}
              showingIndent={hovering || showIndent ? depth : showingIndent}
              rowHoverColor={rowHoverColor}
              italic={true}
            />
          )}
      </>
    );
  },
);
